import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ArtifactPageHeaderProps {
  title: string
  description?: string | null
  onBack: () => void
  /** Says where the back arrow goes ("Back to sections"): its accessible name and tooltip. */
  backLabel?: string
  /** Slot to the right of the title — badges, type label, etc. */
  titleBadges?: ReactNode
  /** Inline owner selector (or any small avatar-sized element) placed next to the title. */
  owner?: ReactNode
  /** Right-side toolbar slot — primary action, overflow menu, reviewer picker, etc. */
  actions?: ReactNode
  /** Optional save status to surface autosave activity inline. */
  saveStatus?: {
    isSaving: boolean
    /** When true (and not saving) briefly surfaces a "Saved" confirmation. */
    justSaved?: boolean
  }
}

export function ArtifactPageHeader({
  title,
  description,
  onBack,
  backLabel,
  titleBadges,
  owner,
  actions,
  saveStatus,
}: ArtifactPageHeaderProps) {
  return (
    <div className="px-6 py-3">
      <div className="flex items-center justify-between gap-4">
        {/* Left: back + title + owner + description */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <BackButton onBack={onBack} label={backLabel} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-sm font-medium truncate">{title}</h1>
              {owner && <div className="shrink-0">{owner}</div>}
              {titleBadges}
            </div>
            {description && (
              <p
                className="text-meta truncate max-w-2xl mt-0.5"
                title={description}
              >
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Right: save status + actions */}
        <div className="flex items-center gap-2 shrink-0">
          <SaveStatusIndicator status={saveStatus} />
          {actions}
        </div>
      </div>
    </div>
  )
}

function SaveStatusIndicator({ status }: { status?: ArtifactPageHeaderProps['saveStatus'] }) {
  if (!status) return null
  if (status.isSaving) {
    return (
      <span className="flex items-center gap-1.5 text-meta" aria-live="polite">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    )
  }
  if (status.justSaved) {
    return (
      <span className={cn('text-meta')} aria-live="polite">
        Saved
      </span>
    )
  }
  return null
}

function BackButton({ onBack, label }: { onBack: () => void; label?: string | undefined }) {
  const button = (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0"
      onClick={onBack}
      aria-label={label}
    >
      <ArrowLeft className="h-4 w-4" />
    </Button>
  )
  if (!label) return button
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
